import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getZombieInstanceFromPrimaryKey } from '@iotv/datamodel';
import { DeviceTypes } from '@iotv/iotv-v3-types';
import { Box, Button, CardContent, InputAdornment, Modal, Stack, TextField, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { QrReader } from 'react-qr-reader';
import { useParams } from 'react-router-dom';
import config from '../../config';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import AppDao from '../../data/AppDao';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { camelCase_to_Sentence, toTitleCase } from '../../helpers/format';
import { RelativeError } from '../Invalid';
import { DeviceLinkContext } from './DeviceLinkContext';
import DeviceRelations from './DeviceRelations';
const dev = true;
const debug = process.env.REACT_APP_DEBUG === 'true' && false;
const supportEmail = config.app.webUI.supportEmail;
const prodView = config.app.stage === 'prod';
const NO_DEVICE = 'No Device Found';
const DEFAULT_EUI_SUBSTRING = '70B3D516B000';
const EUI_LENGTH = 16;
const getDeviceType = (vbo) => {
    let devTypeNumber = undefined;
    if (vbo.deviceType) {
        devTypeNumber = vbo.deviceType;
        return DeviceTypes[devTypeNumber];
    }
    return NO_DEVICE;
};
const LinkingDeviceDetails = (props) => {
    const { clear, resetter, setDevice, setAllowEntities } = props;
    const { device, entityOptions } = useContext(DeviceLinkContext);
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { eui } = useParams();
    const euiEditable = eui === undefined;
    const [EUI, setEUI] = useState(DEFAULT_EUI_SUBSTRING);
    const [requery, setRequery] = useState(0);
    const [relatedDetails, setRelatedDetails] = useState();
    const [loading, setLoading] = useState(true);
    const [scanner, setScanner] = useState(false);
    const [noCamera, setNoCamera] = useState('');
    const [invalidEUI, setInvalidEUI] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (eui) {
                setEUI(eui);
            }
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [eui]);
    const checkCameraPermissions = () => {
        navigator.permissions.query({ name: 'camera' }).then((res) => {
            if (res.state == 'granted') {
                // has permission
                navigator.mediaDevices
                    .getUserMedia({ video: { facingMode: { exact: 'environment' } } })
                    .then((mediaRes) => {
                    setNoCamera('');
                    setScanner(true);
                })
                    .catch((error) => {
                    dev && console.log('error', error);
                    // error when there is only 1 camera (due to facingMode)
                    if (error.name === 'OverconstrainedError') {
                        setNoCamera('');
                        setScanner(true);
                    }
                    else {
                        setNoCamera(error.message);
                        setScanner(false);
                    }
                });
            }
            else {
                setNoCamera('Please enable camera permissions');
                setScanner(false);
            }
        });
    };
    const setDisplayEUI = (potentialEUI) => {
        const formatted = potentialEUI
            .replace(/[^0-9a-fA-F]/g, '')
            .toUpperCase()
            .substring(0, 16);
        // clear if it was 'filled'
        if (EUI.length === EUI_LENGTH && formatted.length !== EUI_LENGTH) {
            clear();
        }
        if (prodView && formatted.length < 12) {
            setEUI(DEFAULT_EUI_SUBSTRING);
        }
        else {
            setEUI(formatted);
        }
    };
    useEffect(() => {
        setEUI(DEFAULT_EUI_SUBSTRING);
    }, [resetter]);
    useEffect(() => {
        const getDeviceFromDB = async (VBO) => {
            const retrievedDBObj = await AppDao.getOne(VBO);
            if (retrievedDBObj.err === null && retrievedDBObj.data !== null) {
                return retrievedDBObj.data;
            }
            else {
                return undefined;
            }
        };
        if (EUI.length === 16) {
            setLoading(true);
            const zombieObj = getZombieInstanceFromPrimaryKey(`Device:${EUI}`);
            getDeviceFromDB(zombieObj).then((res) => {
                if (res === undefined) {
                    setInvalidEUI(true);
                }
                else {
                    setDevice && setDevice(res);
                    setInvalidEUI(false);
                }
                setLoading(false);
            });
        }
        else {
            setDevice(undefined);
            setInvalidEUI(false);
        }
    }, [EUI]);
    useEffect(() => {
        const triggerDetails = async (input) => {
            const res = await ConsumerDao.getDeviceContext({ device: input });
            if (res.data) {
                setRelatedDetails(res.data.nodeEdgeMapper);
            }
            else {
                setRelatedDetails(undefined);
            }
        };
        if (device) {
            triggerDetails(device);
        }
        triggerDetails;
    }, [device, requery]);
    return (_jsxs("div", { children: [_jsxs(Grid, { id: 'Device-EUI', container: true, size: { xs: 12 }, spacing: 1, sx: {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }, children: [_jsxs(Grid, { size: { xs: 12 }, container: true, direction: "row", sx: {
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }, children: [_jsx(Grid, { size: { xs: 3 }, children: _jsx(Typography, { variant: "body2", component: "p", color: "primary", children: `2) Set EUI` }) }), _jsx(Grid, { size: { xs: 7 }, sx: { flexGrow: 1 }, children: _jsx(TextField, { disabled: !euiEditable, size: 'small', value: eui ?? EUI, onChange: (e) => setDisplayEUI(e.target.value), slotProps: {
                                        input: {
                                            endAdornment: EUI.length !== 16 && _jsx(InputAdornment, { position: "end", children: `|${16 - EUI.length}` }),
                                        },
                                    } }) })] }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Button, { fullWidth: true, variant: "contained", onClick: () => {
                                navigator.mediaDevices
                                    .getUserMedia({
                                    video: true,
                                })
                                    .then((stream) => {
                                    // camera was found. Stop previously running cameras
                                    stream.getTracks().forEach((track) => track.stop());
                                    checkCameraPermissions();
                                    /* use the stream */
                                })
                                    .catch((err) => {
                                    // Should be failed by default, but just in case
                                    checkCameraPermissions();
                                });
                            }, children: "Scan" }) }), _jsx("br", {})] }), invalidEUI && (_jsx(Grid, { id: 'EUI-does-not-exist', container: true, size: { xs: 12 }, spacing: 2, sx: {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }, children: _jsx(Box, { sx: { border: 1, borderColor: 'red', borderRadius: '10px' }, children: _jsxs(CardContent, { children: [_jsxs("div", { children: ["Device ", _jsx("u", { children: EUI }), " does not exist"] }), _jsxs("div", { children: ["Please contact ", _jsx("a", { href: `mailto:${supportEmail}`, children: supportEmail }), " or your reseller"] })] }) }) })), _jsx(Grid, { id: 'Device-Link-Already-Exist', container: true, size: { xs: 12 }, spacing: 2, sx: {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }, children: device && relatedDetails && entityOptions && !loading && _jsx(DeviceRelations, { relations: relatedDetails, setAllowEntities: setAllowEntities }) }), noCamera !== '' && _jsx(RelativeError, { title: 'Camera Issue!', error: noCamera }), _jsxs(Grid, { id: 'Device-Type', container: true, size: { xs: 12 }, spacing: 2, sx: {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    height: '50px',
                }, children: [_jsx(Grid, { children: _jsx(Typography, { variant: "body2", component: "p", color: "primary", children: `3) Device Type Found` }) }), _jsx(Grid, { children: device ? (_jsxs("b", { children: [toTitleCase(camelCase_to_Sentence(getDeviceType(device))), " (", device.deviceType, ")"] })) : (NO_DEVICE) })] }), scanner && (_jsx(Modal, { open: scanner, onClose: () => setScanner(false), children: _jsxs(Stack, { direction: "column", sx: {
                        justifyContent: 'center',
                        alignItems: 'center',
                    }, children: [_jsx("br", {}), _jsx("br", {}), _jsx("div", { children: "Not here?" }), _jsx(QrReader, { onResult: (result, error) => {
                                if (result) {
                                    if (result.getText()) {
                                        const euiScanned = result.getText().slice(-16);
                                        setDisplayEUI(euiScanned);
                                        setScanner(false);
                                    }
                                }
                                if (error) {
                                    console.info(error);
                                }
                            }, containerStyle: { minWidth: isSmallView ? '360px' : '600px' }, constraints: { facingMode: 'environment' } }), _jsx(Button, { style: { minWidth: isSmallView ? '360px' : '600px' }, color: "secondary", variant: "contained", onClick: () => setScanner(false), children: "Close" })] }) }))] }));
};
export default LinkingDeviceDetails;
